import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface TabDef {
  readonly id: string;
  readonly label: string;
}

/**
 * Onglets WAI-ARIA : flèches gauche/droite, Home/End, Enter/Space.
 * Émet `cof2-tab-change` (detail: { id }).
 */
@customElement('cof2-tabs')
export class Cof2Tabs extends LitElement {
  @property({ type: Array }) tabs: readonly TabDef[] = [];
  @property({ type: String }) active = '';
  @state() private focused = '';

  static styles = css`
    :host {
      display: block;
    }
    .tablist {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-2);
      border-bottom: 1px solid var(--color-border);
      margin-bottom: var(--space-4);
    }
    button {
      all: unset;
      cursor: pointer;
      padding: var(--space-2) var(--space-4);
      border-radius: var(--radius-sm) var(--radius-sm) 0 0;
      color: var(--color-fg-muted);
      border-bottom: 2px solid transparent;
      transition: color var(--dur-quick), border-color var(--dur-quick);
    }
    button:hover {
      color: var(--color-fg);
    }
    button[aria-selected='true'] {
      color: var(--color-accent);
      border-bottom-color: var(--color-accent);
    }
    button:focus-visible {
      outline: 2px solid var(--color-accent);
      outline-offset: -2px;
    }
  `;

  protected willUpdate(changed: Map<string, unknown>): void {
    if (!this.focused && this.active) this.focused = this.active;
    if (changed.has('active') && this.active) this.focused = this.active;
  }

  private select(id: string): void {
    this.active = id;
    this.focused = id;
    this.dispatchEvent(new CustomEvent('cof2-tab-change', { detail: { id }, bubbles: true }));
  }

  private onKey(e: KeyboardEvent, idx: number): void {
    const n = this.tabs.length;
    if (n === 0) return;
    let next = idx;
    if (e.key === 'ArrowRight') next = (idx + 1) % n;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    else return;
    e.preventDefault();
    const tab = this.tabs[next];
    if (tab) {
      this.select(tab.id);
      const btn = this.renderRoot.querySelectorAll<HTMLButtonElement>('button')[next];
      btn?.focus();
    }
  }

  render() {
    return html`
      <div class="tablist" role="tablist">
        ${this.tabs.map((t, i) => {
          const selected = t.id === this.active;
          return html`
            <button
              id="tab-${t.id}"
              role="tab"
              aria-selected=${selected}
              tabindex=${selected ? '0' : '-1'}
              @click=${() => this.select(t.id)}
              @keydown=${(e: KeyboardEvent) => this.onKey(e, i)}
            >
              ${t.label}
            </button>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cof2-tabs': Cof2Tabs;
  }
}
